import { postRequest, getRequest, putRequest, deleteRequest, BaseApi } from "lib/core/apiClient";
import { getAccessToken } from "lib/utils/auth";
import { getDeviceInfo, getQueryParams, utmParamKeys } from "lib/utils/helpers";
import { isInstagramBrowser } from "lib/utils/helpers";
import { PaymentModeType } from "lib/types/payments";
import { paymentModeNextStep, isDefaultImplementationMode } from "lib/utils/payments";
import { getCheckoutExpiredParams } from "lib/utils/checkout";
import {
  ResolveDiscountAchieverRequest,
  ResolveDiscountAchieverResponse,
  ResolveDiscountAchieverPageRequest,
  ResolveDiscountAchieverPageResponse,
  VariantCatalogEntry,
} from "lib/types/discountAchiever";
import {
  GiftCardApplyRequestType,
  GiftCardBalanceRequestType,
  GiftCardBalanceResponseType,
  GiftCardWalletRechargeRequestType,
  GiftCardWalletRechargeResponseType,
} from "lib/types/giftCards";

export const validatePayment = async (
  checkoutId: string,
  paymentMode: PaymentModeType,
  merchantId: string,
  merchantName: string,
  { ...rest },
  sendAnalyticsEvent?: any,
) => {
  try {
    const response = await getRequest(`/checkout/v1/checkout/${checkoutId}/payments/status`);
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getPaymentStatus = async (checkoutId: string) => {
  try {
    // Updating the base url to kratos_private base-url.
    const response = await getRequest(`/checkout/${checkoutId}/payments/status`, "KRATOS_PRIVATE");
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};


export const applyGiftCard = async (checkoutId: string, payload: GiftCardApplyRequestType) => {
  try {
    const response = await postRequest(
      `/checkout/${checkoutId}/gift-cards`,
      payload,
      "KRATOS_PRIVATE",
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};


export const getGiftCardBalance = async (
  checkoutId: string,
  payload: GiftCardBalanceRequestType,
): Promise<GiftCardBalanceResponseType> => {
  try {
    const response: GiftCardBalanceResponseType = await postRequest(
      `/checkout/${checkoutId}/gift-cards/balance`,
      payload,
      "KRATOS_PRIVATE",
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const removeGiftCard = async (checkoutId: string, transactionId: string) => {
  try {
    const response = await deleteRequest(
      `/checkout/${checkoutId}/gift-cards/${transactionId}`,
      undefined,
      "KRATOS_PRIVATE",
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};


export const rechargeGiftCardWallet = async (
  checkoutId: string,
  payload: GiftCardWalletRechargeRequestType,
): Promise<GiftCardWalletRechargeResponseType> => {
  try {
    const response: GiftCardWalletRechargeResponseType = await postRequest(
      `/checkout/${checkoutId}/gift-cards/wallet/recharge`,
      payload,
      "KRATOS_PRIVATE",
    );
    return response;
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export const getDetailsByZip = async (zip: string) => {
  if (!Boolean(zip)) return {};
  try {
    const response = await getRequest(`/v1/pincodes/${zip}`, "CHECKOUT_PUBLIC");
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/**
 * Method to check if Default Razorpay gateway or Custom implementation of Razorpay to be loaded.
 * The check is based on the past failue rates of the loaded device.
 * Default Razorpay payment gateway will be loaded if failur rate on a particular device exceeds the configured threshold value.
 *
 * @return {boolean} true/false   true if Default Razorpay gateway to be loaded, false if custom implementation to be loaded
 */
export const isRzpModalRequired = async () => {
  if (isInstagramBrowser()) return true;
  return false;
  // const deviceInfo = getDeviceInfo();
  // try {
  //   const response = await getRequest(`/devices/${deviceInfo}/compatibility`, true);
  //   const threshold = constants.PAYMENT_FAILURE_THRESHOLD;
  //   return response?.count > threshold;
  // } catch (e) {
  //   console.error(e);
  //   throw e;
  // }
};

/**
 * Method to put a verification tag for the placed order.
 *
 * @return {response}    Returns verfication response object
 */
export const setVerifiedFlag = async (addressId: string | undefined) => {
  try {
    const response = await putRequest(`/gatekeeper/v1/accounts/addresses/${addressId}/verify`, {});
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/**
 * Method to update UTM parameters for ACR checkouts
 *
 * @param  {string} checkoutId          checkout ID
 */
export const sendCheckoutUTMParameters = async (checkoutId: string) => {
  if (!Boolean(checkoutId)) return;
  try {
    const queryParams = window.location.search;
    let utmParams = [];
    if (
      utmParamKeys?.some(function (param) {
        return queryParams.indexOf(param) >= 0;
      })
    ) {
      utmParams = getQueryParams(queryParams);
      utmParams = utmParams?.filter((param: any) => utmParamKeys.includes(param.name));
    }

    if (!Boolean(utmParams?.length)) return;
    const payload = {
      note_attributes: utmParams,
    };

    await putRequest(`/checkout/v1/checkout/${checkoutId}/note-attributes`, payload);
  } catch (e) {
    console.error(e);
    return;
  }
};

/**
 * Method to get Address fields config by Country Code
 *
 * @param  {string} countryCode          Country Code
 *
 * @param  {string} merchantId           Merchant ID
 */
export const getAddressConfigByCountryCode = async (countryCode: string, merchantId: string) => {
  if (!countryCode || !merchantId) return {};
  try {
    const response = await getRequest(
      `/v1/merchants/${merchantId}/address-config?country_code=${countryCode}`,
      "CHECKOUT_PUBLIC",
    );
    return response;
  } catch (e) {
    console.error(e);
    return {};
  }
};

/**
 * Method to get Provinces/State Codes by Country Code
 *
 * @param  {string} countryCode          Country Code
 *
 */
export const getProvincesByCountryCode = async (countryCode: string) => {
  if (!countryCode) return [];
  try {
    const response = await getRequest(`/v1/countries/${countryCode}/provinces`, "CHECKOUT_PUBLIC");
    return response;
  } catch (e) {
    console.error(e);
    return [];
  }
};

/**
 * Initiates a payment method action by calling the backend API.
 *
 * @param method - The selected payment mode
 * @param checkoutId - The unique identifier of the current checkout
 *
 * @returns A promise that resolves to the response from the API or undefined if arguments are missing.
 * @throws An error if the API call fails.
 */
const normalizeKratosInitiateResult = (result: any) => {
  if (!result || typeof result !== "object") return result;
  return { ...result, pg: result.pg_type ?? result.pg, pg_data: result.order_data ?? result.pg_data };
};

export const getPaymentMethodAction = async ({
  method,
  checkoutId,
  context,
  additionalPayload,
  useKratosPaymentFlow,
  setCheckoutExpired,
}: {
  method: PaymentModeType;
  checkoutId: string;
  context?: string;
  additionalPayload?: any;
  useKratosPaymentFlow?: boolean;
  setCheckoutExpired?: (expired: boolean, isInvalidUser: boolean) => void;
}): Promise<any | undefined> => {
  if (!checkoutId || !method) {
    // Return early with an undefined value if required arguments are missing.
    return;
  }

  if (method === "SPLIT_COD") {
    let paymentResponse;
    try {
      paymentResponse = await getRequest(
        `/checkout/${checkoutId}/payments?payment_context=SPLIT_COD`,
        "KRATOS_PRIVATE",
      );
    } catch (e) {
      console.error(e);
    } finally {
      return paymentResponse;
    }
  }

  const payload = {
    payment_mode: method,
    action: paymentModeNextStep(method),
    ...(isDefaultImplementationMode(method) && { implementation: "DEFAULT" }),
    ...(context && { payment_context: context }),
    ...additionalPayload,
  };

  try {
    if (useKratosPaymentFlow) {
      const result = await postRequest(
        `/checkout/${checkoutId}/payments/initiate`,
        payload,
        "KRATOS_PRIVATE",
      );
      return normalizeKratosInitiateResult(result);
    }
    const response = await postRequest(`/checkout/v2/checkout/${checkoutId}/payments/initiate`, payload);
    return response;
  } catch (error: any) {
    const rawError = error?.response?.data?.error;
    const errorMessage = typeof rawError === "string" ? rawError : rawError?.message;
    if (
      error?.response?.status === 400 &&
      (errorMessage === "C2P flow timedout" ||
        errorMessage === "C2P is already completed" ||
        errorMessage === "C2P flow inaccessible due to user change" ||
        Boolean(errorMessage?.toLowerCase().includes("c2p")))
    ) {
      const { expired, isInvalidUser } = getCheckoutExpiredParams(errorMessage ?? "");
      setCheckoutExpired?.(expired, isInvalidUser);
    }
    console.error("Error while getting Payment Method details: ", error);
    throw error;
  }
};

export const addItemToWishlist = async (merchantId: string, variantId: string) => {
  const payload = {
    merchant_id: merchantId,
    variant_id: variantId,
  };
  try {
    const response = await postRequest(`/santa/v1/wishlists`, payload);
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const removeItemFromWishlist = async (merchantId: string, variantId: string) => {
  const payload = {
    merchant_id: merchantId,
    variant_id: variantId,
  };
  try {
    const response = await deleteRequest(`/santa/v1/wishlists`, payload);
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

export const getWishlistedProducts = async (merchantId: string) => {
  try {
    const response = await getRequest(`/santa/v1/wishlists?merchant_id=${merchantId}`);
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

/** Shoppers can open the discount achiever before logging in, so fall back to the public CE
    routes (no authorizer) whenever we have no access token. */
const getDiscountAchieverBaseApi = (): BaseApi => (getAccessToken() ? "CE" : "CE_PUBLIC");

export const resolveDiscountAchiever = async (
  merchantId: string,
  payload: ResolveDiscountAchieverRequest,
): Promise<ResolveDiscountAchieverResponse> => {
  try {
    const response = await postRequest(
      `/merchant/${merchantId}/product/discount-achiever/resolve`,
      payload,
      getDiscountAchieverBaseApi(),
    );
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};

const CE_ISSUER_ID = import.meta.env.VITE_ISSUER_ID;

/**
 * Resolves variant ids to variants with their parent product (and therefore the product's full
 * sibling variant list) inlined.
 *
 * The issuer id stays in the path deliberately — the lookup caches on issuer + merchant + variant,
 * so an issuer-less path reads a partition that never gets invalidated and serves stale prices.
 *
 * Ids that do not exist are dropped server-side, so the result can be shorter than `variantIds`.
 * Always match on `variant_id`, never by index.
 */
export const fetchVariantsByIds = async (
  merchantId: string,
  variantIds: string[],
): Promise<VariantCatalogEntry[]> => {
  const ids = Array.from(new Set((variantIds ?? []).filter(Boolean)));
  if (!Boolean(ids.length) || !Boolean(merchantId)) return [];

  // Route on whether an access token actually exists, not on the isAuthenticated flag — the flag
  // can be true for a tick before the token lands (or after it expires), which would otherwise
  // hit the JWT-gated route unauthenticated and 401.
  const hasAccessToken = Boolean(getAccessToken());
  const path = hasAccessToken
    ? `/issuer/${CE_ISSUER_ID}/merchant/${merchantId}/variant`
    : `/issuer/${CE_ISSUER_ID}/merchant/${merchantId}/variant/bulk`;
  const baseApi = hasAccessToken ? "CE" : "CE_PUBLIC";

  try {
    const response: VariantCatalogEntry[] | null = await postRequest(path, { variant_ids: ids }, baseApi);
    return response ?? [];
  } catch (e) {
    console.error("Error while fetching variants: ", e);
    throw e;
  }
};

export const resolveDiscountAchieverPage = async (
  merchantId: string,
  payload: ResolveDiscountAchieverPageRequest,
): Promise<ResolveDiscountAchieverPageResponse> => {
  try {
    const response = await postRequest(
      `/merchant/${merchantId}/product/discount-achiever/resolve/page`,
      payload,
      getDiscountAchieverBaseApi(),
    );
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
